import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { CameraOffIcon } from '@vlabs/icons';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import st from './FullFrame.module.sass';
import { useFullFrameTranslation } from './i18n';
import { buildTestid } from '../../lib/utils';

export interface Size {
  containerSize: {
    width: number;
    height: number;
  };
  imageSize: {
    width: number;
    height: number;
  };
}

type ImageStatus = 'loading' | 'loaded' | 'error';

export interface FullFrameProps extends WithDataTestid, WithClassNameProp {
  src?: string;
  children?: React.ReactNode | ((size: Size) => React.ReactNode);
}

const displayName = 'FullFrame';
const testid = buildTestid(displayName);

function FullFrame({
  src = undefined,
  children = undefined,
  className = undefined,
  'data-testid': dataTestId,
}: FullFrameProps): React.JSX.Element {
  const { t } = useFullFrameTranslation();
  const [size, setSize] = useState<Size | null>(null);
  const [status, setStatus] = useState<ImageStatus>('error');
  const imageRef = useRef<HTMLImageElement>(null);

  const updateSize = useCallback(() => {
    const image = imageRef.current;

    if (!image) return;

    const {
      offsetWidth,
      offsetHeight,
      naturalWidth,
      naturalHeight,
    } = image;

    if (!naturalWidth || !naturalHeight) return;

    setSize({
      containerSize: {
        width: offsetWidth,
        height: offsetHeight,
      },
      imageSize: {
        width: naturalWidth,
        height: naturalHeight,
      },
    });
  }, []);

  useEffect(() => {
    if (!src) {
      setStatus('error');
      setSize(null);
      return;
    }

    const image = imageRef.current;

    if (image?.complete && image.naturalWidth > 0) {
      setStatus('loaded');
      updateSize();
    } else {
      setStatus('loading');
    }
  }, [src, updateSize]);

  useEffect(() => {
    const image = imageRef.current;

    if (!image) return;

    const observer = new ResizeObserver(updateSize);
    observer.observe(image);

    return () => observer.disconnect();
  }, [status, updateSize]);

  const handleImageLoad = () => {
    setStatus('loaded');
    updateSize();
  };

  const handleImageError = () => {
    setStatus('error');
    setSize(null);
  };

  return (
    <div className={cn(className, st.Root)}>
      {!!(!src || status === 'error') && (
        <div className={st.NoImageDummy}>
          <CameraOffIcon />
          {t('empty')}
        </div>
      )}

      {!!src && (
        <div className={st.ImageWrapper}>
          <img
            alt="FullFrame"
            onError={handleImageError}
            onLoad={handleImageLoad}
            ref={imageRef}
            src={src}
            {...testid(dataTestId)}
          />
          {status === 'loaded' && !!size && !!children && (typeof children === 'function'
            ? children(size)
            : children)}
        </div>
      )}
    </div>
  );
}

FullFrame.displayName = displayName;

export { FullFrame };
